import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { quotesApi, clientsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Plus, Search, MoreVertical, FileText, Eye, Send, 
  ArrowRightLeft, CheckCircle, XCircle, Trash2, Loader2,
  Calendar, DollarSign, User, Clock
} from "lucide-react";

const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createDialog, setCreateDialog] = useState(false);
  const [convertDialog, setConvertDialog] = useState({ open: false, quote: null });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const [newQuote, setNewQuote] = useState({
    client_id: "",
    valid_days: 15,
    payment_conditions: "",
    notes: "",
    items: [{ description: "", quantity: 1, unit_price: 0, unit: "E48", clave_producto: "80101500", discount_percent: 0 }],
    include_iva: true,
    iva_rate: 0.16
  });

  const [convertForm, setConvertForm] = useState({
    payment_method: "PUE",
    payment_form: "03"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [quotesRes, clientsRes] = await Promise.all([
        quotesApi.list(),
        clientsApi.list()
      ]);
      setQuotes(quotesRes.data.quotes || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async () => {
    if (!newQuote.client_id) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (newQuote.items.length === 0 || !newQuote.items[0].description) {
      toast.error("Agrega al menos un concepto");
      return;
    }

    setCreating(true);
    try {
      await quotesApi.create(newQuote);
      toast.success("Cotizacion creada exitosamente");
      setCreateDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cotizacion");
    } finally {
      setCreating(false);
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await quotesApi.send(quoteId);
      toast.success("Cotizacion enviada al cliente");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar cotizacion");
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      await quotesApi.accept(quoteId);
      toast.success("Cotizacion marcada como aceptada");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aceptar cotizacion");
    }
  };

  const handleRejectQuote = async (quoteId) => {
    try {
      await quotesApi.reject(quoteId);
      toast.success("Cotizacion marcada como rechazada");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar cotizacion");
    }
  };

  const handleConvertClick = (quote) => {
    setConvertDialog({ open: true, quote });
    setConvertForm({ payment_method: "PUE", payment_form: "03" });
  };

  const handleConvertToInvoice = async () => {
    try {
      const result = await quotesApi.convert(convertDialog.quote.id, convertForm);
      toast.success(result.data.message || "Cotizacion convertida a factura");
      setConvertDialog({ open: false, quote: null });
      loadData();
      // Navigate to invoices
      navigate("/dashboard/invoices");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al convertir cotizacion");
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Estas seguro de eliminar esta cotizacion?")) return;
    try {
      await quotesApi.delete(quoteId);
      toast.success("Cotizacion eliminada");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar cotizacion");
    }
  };

  const resetForm = () => {
    setNewQuote({
      client_id: "",
      valid_days: 15,
      payment_conditions: "",
      notes: "",
      items: [{ description: "", quantity: 1, unit_price: 0, unit: "E48", clave_producto: "80101500", discount_percent: 0 }],
      include_iva: true,
      iva_rate: 0.16
    });
  };

  const addItem = () => {
    setNewQuote({
      ...newQuote,
      items: [...newQuote.items, { description: "", quantity: 1, unit_price: 0, unit: "E48", clave_producto: "80101500", discount_percent: 0 }]
    });
  };

  const removeItem = (index) => {
    if (newQuote.items.length === 1) return;
    setNewQuote({
      ...newQuote,
      items: newQuote.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const items = [...newQuote.items];
    items[index][field] = value;
    setNewQuote({ ...newQuote, items });
  };

  const calculateTotal = () => {
    const subtotal = newQuote.items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unit_price;
      const discount = itemTotal * (item.discount_percent / 100);
      return sum + (itemTotal - discount);
    }, 0);
    const iva = newQuote.include_iva ? subtotal * newQuote.iva_rate : 0;
    return { subtotal, iva, total: subtotal + iva };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      sent: "bg-blue-100 text-blue-700",
      accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
      expired: "bg-orange-100 text-orange-700",
      converted: "bg-purple-100 text-purple-700"
    };
    const labels = {
      draft: "Borrador",
      sent: "Enviada",
      accepted: "Aceptada",
      rejected: "Rechazada",
      expired: "Expirada",
      converted: "Convertida"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totals = calculateTotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cotizaciones</h1>
          <p className="text-slate-600">Gestiona tus cotizaciones y convierte a factura</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="create-quote-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Cotizacion
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{quotes.length}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{quotes.filter(q => q.status === 'accepted').length}</p>
                <p className="text-sm text-slate-500">Aceptadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ArrowRightLeft className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{quotes.filter(q => q.status === 'converted').length}</p>
                <p className="text-sm text-slate-500">Convertidas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{quotes.filter(q => q.status === 'sent').length}</p>
                <p className="text-sm text-slate-500">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por numero o cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-quotes"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm"
          data-testid="status-filter"
        >
          <option value="all">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="sent">Enviada</option>
          <option value="accepted">Aceptada</option>
          <option value="rejected">Rechazada</option>
          <option value="expired">Expirada</option>
          <option value="converted">Convertida</option>
        </select>
      </div>

      {/* Quotes Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Numero</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Cliente</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Total</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Vigencia</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Estado</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
                      <p>No hay cotizaciones</p>
                      <Button variant="link" onClick={() => setCreateDialog(true)}>
                        Crear primera cotizacion
                      </Button>
                    </td>
                  </tr>
                ) : (
                  filteredQuotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-slate-50" data-testid={`quote-row-${quote.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium">{quote.quote_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{quote.client_name || "Cliente"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">{formatCurrency(quote.total)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {new Date(quote.valid_until).toLocaleDateString('es-MX')}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(quote.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`quote-actions-${quote.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {}}>
                                <Eye className="w-4 h-4 mr-2" />
                                Ver detalle
                              </DropdownMenuItem>
                              
                              {quote.status === 'draft' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleSendQuote(quote.id)}>
                                    <Send className="w-4 h-4 mr-2" />
                                    Enviar al cliente
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDeleteQuote(quote.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Eliminar
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {quote.status === 'sent' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleAcceptQuote(quote.id)}>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Marcar aceptada
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleRejectQuote(quote.id)}>
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Marcar rechazada
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {(quote.status === 'accepted' || quote.status === 'sent') && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleConvertClick(quote)}>
                                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                                    Convertir a factura
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Quote Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Cotizacion</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Client Selection */}
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <select
                value={newQuote.client_id}
                onChange={(e) => setNewQuote({ ...newQuote, client_id: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                data-testid="select-client"
              >
                <option value="">Seleccionar cliente...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.rfc}
                  </option>
                ))}
              </select>
            </div>

            {/* Valid Days */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dias de vigencia</Label>
                <Input
                  type="number"
                  value={newQuote.valid_days}
                  onChange={(e) => setNewQuote({ ...newQuote, valid_days: parseInt(e.target.value) || 15 })}
                  min={1}
                  max={90}
                />
              </div>
              <div className="space-y-2">
                <Label>Condiciones de pago</Label>
                <Input
                  value={newQuote.payment_conditions}
                  onChange={(e) => setNewQuote({ ...newQuote, payment_conditions: e.target.value })}
                  placeholder="Ej: 50% anticipo, 50% contra entrega"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Conceptos</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar
                </Button>
              </div>
              
              {newQuote.items.map((item, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-500">Concepto {index + 1}</span>
                    {newQuote.items.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Input
                      placeholder="Descripcion del producto o servicio"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                        min={0.01}
                        step={0.01}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Precio Unitario</Label>
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        min={0}
                        step={0.01}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Descuento %</Label>
                      <Input
                        type="number"
                        value={item.discount_percent}
                        onChange={(e) => updateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={newQuote.notes}
                onChange={(e) => setNewQuote({ ...newQuote, notes: e.target.value })}
                placeholder="Observaciones o notas adicionales..."
                rows={3}
              />
            </div>

            {/* IVA Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="include_iva"
                checked={newQuote.include_iva}
                onChange={(e) => setNewQuote({ ...newQuote, include_iva: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="include_iva">Incluir IVA (16%)</Label>
            </div>

            {/* Totals */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              {newQuote.include_iva && (
                <div className="flex justify-between text-sm">
                  <span>IVA (16%):</span>
                  <span>{formatCurrency(totals.iva)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(totals.total)}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateQuote} disabled={creating} data-testid="submit-quote">
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear Cotizacion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Invoice Dialog */}
      <Dialog open={convertDialog.open} onOpenChange={(open) => setConvertDialog({ ...convertDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convertir a Factura</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Se creara una factura basada en la cotizacion <strong>{convertDialog.quote?.quote_number}</strong>
            </p>
            
            <div className="space-y-2">
              <Label>Metodo de Pago</Label>
              <select
                value={convertForm.payment_method}
                onChange={(e) => setConvertForm({ ...convertForm, payment_method: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="PUE">PUE - Pago en una sola exhibicion</option>
                <option value="PPD">PPD - Pago en parcialidades o diferido</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Forma de Pago</Label>
              <select
                value={convertForm.payment_form}
                onChange={(e) => setConvertForm({ ...convertForm, payment_form: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="01">01 - Efectivo</option>
                <option value="02">02 - Cheque nominativo</option>
                <option value="03">03 - Transferencia electronica</option>
                <option value="04">04 - Tarjeta de credito</option>
                <option value="28">28 - Tarjeta de debito</option>
                <option value="99">99 - Por definir</option>
              </select>
            </div>

            {convertDialog.quote && (
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Total de la factura:</strong> {formatCurrency(convertDialog.quote.total)}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialog({ open: false, quote: null })}>
              Cancelar
            </Button>
            <Button onClick={handleConvertToInvoice} data-testid="confirm-convert">
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Convertir a Factura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesPage;
